import { createContext, ReactNode, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { createHighlightsStore } from "../pages/media/components/animator/useHighlightsStore";
import { createCuepointsStore } from "../pages/media/components/animator/useCuepointsStore";

const createProjectStore = () => {
  return {
    highlightsStore: createHighlightsStore(),
    cuepointsStore: createCuepointsStore(),
  };
};
// Store instances cache
const storeCache = new Map<string, ReturnType<typeof createProjectStore>>();

// Combined project context
type ProjectContextValue = {
  highlightsStore: ReturnType<typeof createHighlightsStore>;
  cuepointsStore: ReturnType<typeof createCuepointsStore>;
};

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { id: projectId } = useParams<{ id: string }>();
  const previousProjectId = useRef<string | null>(null);

  const store = useMemo(() => {
    if (!projectId) return null;

    // Clean up previous project's store
    if (previousProjectId.current && previousProjectId.current !== projectId) {
      const oldStore = storeCache.get(previousProjectId.current);
      if (oldStore) {
        oldStore.highlightsStore.getState().resetHighlightsStore();
        oldStore.cuepointsStore.getState().resetCuepointsStore();
        // Optionally remove from cache
        storeCache.delete(previousProjectId.current);
      }
    }

    // Get or create store for current project
    if (!storeCache.has(projectId)) {
      storeCache.set(projectId, createProjectStore());
    }

    previousProjectId.current = projectId;
    return storeCache.get(projectId);
  }, [projectId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (projectId) {
        const store = storeCache.get(projectId);
        if (store) {
          store.highlightsStore.getState().resetHighlightsStore();
          store.cuepointsStore.getState().resetCuepointsStore();
          storeCache.delete(projectId);
        }
      }
    };
  }, [projectId]);

  if (!store) {
    return <div>Loading...</div>;
  }

  return (
    <ProjectContext.Provider value={store}>{children}</ProjectContext.Provider>
  );
};
